'use client';

import { useMemo } from 'react';

import { UnitSystem } from '@ravago/shared/page-data/models/config';
import { NormalizedProduct, NormalizedTechnicalProperty } from '@ravago/shared/page-data/models/contentful-mapped';
import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';
import { TableCell } from '@ravago/shared/radiance/components/table/components/TableCell/TableCell';
import { TableHeader } from '@ravago/shared/radiance/components/table/components/TableHeader/TableHeader';
import { TableRow } from '@ravago/shared/radiance/components/table/components/TableRow/TableRow';
import { TableSubheader } from '@ravago/shared/radiance/components/table/components/TableSubheader/TableSubheader';
import { Typography } from '@ravago/shared/radiance/components/typography/components/Typography/Typography';
import { useTranslations } from '@ravago/shared/translations/client';

import { firstBy } from 'thenby';

import { PhysicalPropertiesUtils } from '../../../../utils/product/physical-properties.utils';
import { propertyUnitLabels, notchedImpactConfig } from '../../../shared/physical-property-display/mappers/property-unit-labels.mapper';
import { PhysicalProperty } from '../../../shared/physical-property-display/physical-property';

interface Props {
   products: NormalizedProduct[];
   totalColumns: number;
   unitSystem: UnitSystem;
}

interface PropertyConfig {
   key: string;
   translationKey: string;
   translationPlaceholders?: Record<string, string>;
   unit?: string;
   propertyAccessor: (product: NormalizedProduct) => NormalizedTechnicalProperty[];
   sortFn?: (values: NormalizedTechnicalProperty[]) => NormalizedTechnicalProperty[];
   showBadgeOnFirst?: boolean;
}

const sortByNumericValue = (values: NormalizedTechnicalProperty[]) =>
   values.sort(firstBy((v1, v2) => ((Number.parseFloat(v1.value) ?? 0) > (Number.parseFloat(v2.value) ?? 0) ? 1 : -1)));

const sortFlameRating = (unitSystem: UnitSystem) => (values: NormalizedTechnicalProperty[]) =>
   values.sort(
      firstBy('value', 'desc').thenBy((v1, v2) =>
         PhysicalPropertiesUtils.getNumberFromCondition(unitSystem, v1.condition) >
         PhysicalPropertiesUtils.getNumberFromCondition(unitSystem, v2.condition)
            ? 1
            : -1
      )
   );

const filterByUnitOfMeasure = (unitSystem: UnitSystem) => (values: NormalizedTechnicalProperty[]) => values.filter((v) => v.unit === unitSystem);

const filterByUnitAndTestingMethod = (unitId: string, method: string) => (values: NormalizedTechnicalProperty[]) =>
   values.filter((v) => v.unit === unitId && v?.testingMethod?.includes(method));

export const ProductCompareTechnicalProperties = ({ products, totalColumns, unitSystem }: Props) => {
   const translate = useTranslations();

   const propertyConfigs: PropertyConfig[] = useMemo(
      () => [
         {
            key: 'density',
            translationKey: 'product.information.physical-properties.density',
            unit: propertyUnitLabels.density[unitSystem],
            propertyAccessor: (product) => filterByUnitOfMeasure(unitSystem)(product.density),
            showBadgeOnFirst: false
         },
         {
            key: 'flow-rate',
            translationKey: 'product.information.physical-properties.flow-rate',
            unit: propertyUnitLabels.flowRate[unitSystem],
            propertyAccessor: (product) => filterByUnitOfMeasure(unitSystem)(product.flowRate),
            sortFn: sortByNumericValue,
            showBadgeOnFirst: false
         },
         {
            key: 'flame-rating',
            translationKey: 'product.information.physical-properties.flame-rating',
            propertyAccessor: (product) => filterByUnitOfMeasure(unitSystem)(product.flameRating),
            sortFn: sortFlameRating(unitSystem),
            showBadgeOnFirst: true
         },
         {
            key: 'notched-impact-astm',
            translationKey: 'products-overview-filters-notched-izod-impact-dynamic',
            translationPlaceholders: notchedImpactConfig.ASTM[unitSystem],
            propertyAccessor: (product) => filterByUnitAndTestingMethod(unitSystem, 'ASTM')(product.notchedImpact),
            sortFn: sortByNumericValue,
            showBadgeOnFirst: true
         },
         {
            key: 'notched-impact-iso',
            translationKey: 'products-overview-filters-notched-izod-impact-dynamic',
            translationPlaceholders: notchedImpactConfig.ISO[unitSystem],
            propertyAccessor: (product) => filterByUnitAndTestingMethod(unitSystem, 'ISO')(product.notchedImpactStrength),
            sortFn: sortByNumericValue,
            showBadgeOnFirst: true
         }
      ],
      [unitSystem]
   );

   const renderPropertyValues = (values: NormalizedTechnicalProperty[], config: PropertyConfig) => {
      if (!values?.length) return '-';

      const sortedValues = config.sortFn ? config.sortFn([...values]) : values;

      const getBadge = (index: number, testingMethod?: string) => {
         if (config.showBadgeOnFirst) {
            return index === 0 ? testingMethod : undefined;
         }
         return testingMethod;
      };

      return (
         <Box direction="column" gap="2xs">
            {sortedValues.map((value, index) => (
               <PhysicalProperty
                  key={`${config.key}-${value.value}-${value.testingMethod}-${value.condition}`}
                  value={value.value}
                  badge={getBadge(index, value.testingMethod)}
                  condition={value.condition}
               />
            ))}
         </Box>
      );
   };

   return (
      <>
         <TableRow>
            <TableSubheader colSpan={totalColumns}>{translate('page-consumer.product-compare.table.technical-properties')}</TableSubheader>
         </TableRow>
         {propertyConfigs.map((config) => (
            <TableRow key={config.key}>
               <TableHeader>
                  <Typography variant="body-highlight">
                     {translate(config.translationKey, config.translationPlaceholders)} {config.unit}
                  </Typography>
               </TableHeader>
               {products.map((product) => (
                  <TableCell verticalAlign="top" key={`${config.key}-${product.id}`}>
                     {renderPropertyValues(config.propertyAccessor(product), config)}
                  </TableCell>
               ))}
            </TableRow>
         ))}
      </>
   );
};
